import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { 
  Save, 
  Eye, 
  EyeOff, 
  Settings, 
  RotateCcw, 
  Copy, 
  FileText, 
  Layout,
  Download,
  Upload,
  Plus,
  MoreHorizontal,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Type,
  Palette,
  Link,
  Image,
  Table,
  Undo,
  Redo,
  Search,
  Replace,
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  Trash2
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useContracts } from '../../contexts/ContractContext';
import { Contract } from '../../types/contract';

interface ContractDocument {
  id: string;
  title: string;
  content: string;
  pages: string[]; // Array de páginas
  currentPage: number; // Página atual
  styling: {
    fontFamily: string;
    fontSize: number;
    lineHeight: number;
    textColor: string;
    backgroundColor: string;
    accentColor: string;
  };
  createdAt: string;
  updatedAt: string;
}

const ContractTemplateEditor: React.FC = () => {
  const [document, setDocument] = useState<ContractDocument>({
    id: 'default',
    title: 'Modelo de Contrato',
    content: '',
    pages: [''],
    currentPage: 0,
    styling: {
      fontFamily: 'Courier New',
      fontSize: 10,
      lineHeight: 1.4,
      textColor: '#000000',
      backgroundColor: '#ffffff',
      accentColor: '#0f9d8f'
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [splitView, setSplitView] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [selectedContractId, setSelectedContractId] = useState<string>('');
  const [showContractSelector, setShowContractSelector] = useState(false);
  const editorRef = useRef<any>(null);
  
  // Hook para carregar contratos
  const { contracts, loading: contractsLoading } = useContracts();

  // Função para substituir placeholders pelos dados do contrato
  const replacePlaceholders = (content: string, contract?: Contract) => {
    if (!contract) return content;
    
    const replacements: { [key: string]: string } = {
      '{{contratante}}': contract.contratante || '',
      '{{nacionalidade}}': contract.nacionalidade || '',
      '{{profissao}}': contract.profissao || '',
      '{{rg}}': contract.identidade || '',
      '{{cpf}}': contract.cpf || '',
      '{{telefone}}': contract.telefone || '',
      '{{email}}': contract.email || '',
      '{{endereco}}': contract.endereco || '',
      '{{numero}}': contract.numero || '',
      '{{bairro}}': contract.bairro || '',
      '{{cidade}}': contract.cidade || '',
      '{{estado}}': contract.estado || '',
      '{{cep}}': contract.cep || '',
      '{{pacote}}': contract.pacoteAtividades || '',
      '{{atividades}}': contract.pacoteAtividades || '',
      '{{dataFesta}}': contract.dataFesta || '',
      '{{horaInicio}}': contract.horarioInicioFesta || '',
      '{{horaFim}}': contract.fimRecreacao || '',
      '{{enderecoFesta}}': contract.enderecoFesta || '',
      '{{nomeAniversariante}}': contract.nomeAniversariante || '',
      '{{idadeAniversariante}}': contract.idadeAniversariante?.toString() || '',
      '{{numeroConvidados}}': contract.numeroConvidados?.toString() || '',
      '{{temaFesta}}': contract.temaFesta || '',
      '{{valor}}': contract.valor || '',
      '{{formaPagamento}}': contract.formaPagamento || '',
      '{{observacoes}}': contract.observacoes || ''
    };
    
    let result = content;
    Object.entries(replacements).forEach(([placeholder, value]) => {
      result = result.replace(new RegExp(placeholder, 'g'), value);
    });
    
    return result;
  };

  // Função para gerar preview com imagens fixas
  const getPreviewContent = (pageContent: string) => {
    const baseUrl = window.location.origin;
    const selectedContract = contracts.find(c => c.id === selectedContractId);
    const contentWithData = selectedContract ? replacePlaceholders(pageContent, selectedContract) : pageContent;
    
    return `
    <div style="background: white; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0; border-radius: 0.5rem; overflow: hidden; position: relative; width: 210mm; height: 297mm; transform: scale(0.55); transform-origin: top left; display: flex; flex-direction: column;">
      <!-- Imagem de Cabeçalho -->
      <img src="${baseUrl}/images/cabecalho.jpg" alt="Cabeçalho" style="width: 100%; height: auto; display: block; flex-shrink: 0;" onerror="this.style.display='none';" />
      
      <!-- Área de Conteúdo entre Cabeçalho e Rodapé -->
      <div style="padding: 12mm; padding-bottom: 5px; font-family: 'Courier', 'Courier New', monospace; font-size: 10pt; line-height: 1.4; flex: 1; overflow: hidden;">
        ${contentWithData}
      </div>
      
      <!-- Imagem de Rodapé -->
      <img src="${baseUrl}/images/rodape.jpg" alt="Rodapé" style="width: 100%; height: auto; display: block; flex-shrink: 0;" onerror="this.style.display='none';" />
    </div>
    `;
  };

  // Conteúdo padrão do contrato - EXATAMENTE igual ao da página de detalhes
  const getDefaultContent = () => {
    const baseUrl = window.location.origin;
    return `
    <div style="background: white; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0; border-radius: 0.5rem; overflow: hidden; position: relative; width: 210mm; height: 297mm; transform: scale(0.55); transform-origin: top left;">
      <!-- Imagem de Cabeçalho -->
      <img src="${baseUrl}/images/cabecalho.jpg" alt="Cabeçalho" style="width: 100%; height: auto; display: block;" onerror="this.style.display='none';" />
      
      <div style="padding: 12mm; padding-bottom: 5px; font-family: 'Courier', 'Courier New', monospace; font-size: 10pt; line-height: 1.4;">
        <!-- Cabeçalho -->
        <div style="text-align: center; margin-bottom: 16px;">
          <h1 style="font-weight: bold; font-size: 10pt; margin-bottom: 4px;">
            CONTRATO DE PRESTAÇÃO DE SERVIÇO DE RECREAÇÃO INFANTIL
          </h1>
        </div>

        <!-- Identificação das Partes -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            IDENTIFICAÇÃO DAS PARTES CONTRATANTES
          </h2>
          
          <!-- Contratante -->
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CONTRATANTE:</strong> {{contratante}}, {{nacionalidade}}, {{estadoCivil}}, {{profissao}}, RG nº {{identidade}}, C.P.F. nº {{cpf}}, Tel. {{telefone}}, residente e domiciliado na {{endereco}}, nº {{numero}}{{complemento}}, Bairro {{bairro}}, Cep {{cep}}, Cidade {{cidade}}, no Estado {{estado}}.
            </p>
          </div>

          <!-- Contratada -->
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CONTRATADA:</strong> Play Kids Recreação Infantil, com sede em Manhuaçu, na Rua Faustino Amâncio, nº 91, bairro Santo Antônio, Cep 36.900-000, no Estado de MG, C.N.P.J. 31.692.815/0001-02, neste ato representada pelo seu diretor RODRIGO PEREIRA DE OLIVEIRA, brasileiro, Casado, Professor, Carteira de Identidade nº MG 12 682 959, C.P.F. nº 016.331.906-50, residente e domiciliado na Rua Faustino Amâncio, nº 91 bairro Santo Antônio, Cep 36.900-157, Cidade Manhuaçu, no Estado MG.
            </p>
          </div>

          <p style="text-align: justify; margin-bottom: 4px;">
            As partes acima identificadas têm, entre si, justo e acertado o presente Contrato de Prestação de Serviço de Recreação Infantil, que se regerá pelas cláusulas seguintes e pelas condições de pagamento descritas no presente.
          </p>
        </div>

        <!-- Objeto do Contrato -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DO OBJETO DO CONTRATO
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 1ª</strong> - O presente instrumento tem como objeto a prestação de serviços de recreação infantil.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 2ª</strong> - O Pacote escolhido foi o: {{pacoteAtividades}}, contendo as seguintes atividades:
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="font-weight: bold; text-align: justify; margin-bottom: 4px;">
              {{temaFesta}}
            </p>
          </div>
        </div>

        <!-- Da Festa -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DA FESTA
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 3ª</strong> - A festa será realizada no dia {{dataFesta}} às {{horarioInicioFesta}} horas, no(a) endereço {{enderecoFesta}}.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 4ª</strong> - A recreação terá início às {{inicioRecreacao}} horas, encerrando-se às {{fimRecreacao}} horas.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>Parágrafo único.</strong> O tempo previsto para início e término do serviço deverá ser respeitado e em caso de atraso no início por parte do CONTRATANTE, a CONTRATADA se reserva o direito de encerrar as atividades no horário previsto.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 5ª</strong> - Se o CONTRATANTE desejar ampliar o horário da recreação, deverá ser em comum acordo com a CONTRATADA, e caso haja acordo, será cobrada taxa extra no valor de R$ 450,00. (não ficamos fração de hora).
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 6ª</strong> - A quantidade de crianças será de aproximadamente {{numeroConvidados}}, devendo este número ser comunicado à CONTRATADA com 5 dias de antecedência.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 7ª</strong> - O aniversariante chama-se {{nomeAniversariante}}, o tema da festa é {{temaFesta}}, os pais do aniversariante {{restricaoMusical}} restrição a músicas.
            </p>
          </div>
        </div>

        <!-- Obrigações do Contratante -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DAS OBRIGAÇÕES DO CONTRATANTE
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 8ª</strong> - O CONTRATANTE compromete-se a manter a disposição da CONTRATADA todos os meios necessários para execução dos serviços, ou seja, energia elétrica, iluminação, 04 cadeiras, local adequado para a realização das atividades e lanche para os Recreadores.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 9ª</strong> - Se o evento for realizado em condomínio ou prédio, o CONTRATANTE deverá avisar com antecedência aos seguranças e à portaria a chegada dos funcionários da CONTRATADA, facilitando a entrada com veículo nas dependências, para carga e descarga de material.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 10ª</strong> - O CONTRATANTE deverá efetuar o pagamento na forma e condições estabelecidas na cláusula 14ª.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 11ª</strong> - O CONTRATANTE deverá avisar com antecedência de no mínimo 5 dias em caso de alteração da data e horário da festa para que seja acordada nova data e horário, disponível com a agenda da CONTRATADA, desde que haja disponibilidade na mesma.
            </p>
          </div>
        </div>

        <!-- Obrigações da Contratada -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DAS OBRIGAÇÕES DA CONTRATADA
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 12ª</strong> - É dever da CONTRATADA oferecer ao contratante a cópia do presente instrumento, contendo todas as especificidades da prestação de serviço contratado.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 13ª</strong> - Se houver atraso por parte da CONTRATADA, esta deverá compensar ao final da recreação.
            </p>
          </div>
        </div>

        <!-- Preço e Condições de Pagamento -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DO PREÇO E DAS CONDIÇÕES DE PAGAMENTO
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 14ª</strong> - O presente serviço será remunerado pela quantia de R$ {{valor}} ({{valorPorExtenso}}), devendo ser pago no ato da assinatura do contrato. O contrato só terá valor mediante envio do comprovante de pagamento do valor estipulado acima. Conta para transferência:
            </p>
          </div>

          <!-- Dados Bancários -->
          <div style="background-color: #f8fafc; padding: 12px; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 4px;">
            <div style="text-align: right; margin-bottom: 8px;">
              <strong style="color: #0f9d8f;">BANCO CC CREDILIVRE LTDA.</strong>
            </div>
            <div>
              <p>SICOOB CREDILIVRE, AG 3049, CONTA CORRENTE 57 559 3, BANCO: 756, CNPJ: 31 692</p>
              <p>815 0001-02 ou pode ser enviado através do PIX 31 692 815 0001 02</p>
              <p style="text-align: right; color: #64748b;">(conferir cuidadosamente as informações antes de transferir).</p>
            </div>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>Parágrafo único.</strong> Pagamentos onde houver parcelas, o mesmo deverá ser feito no dia estipulado, o não pagamento em dia acarretará multa diária de R$ 15,00.
            </p>
          </div>
        </div>

        <!-- Rescisão e Forças Maiores -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DA RESCISÃO IMOTIVADA ou POR CASOS FORTUITOS e FORÇAS MAIORES
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 15ª</strong> - Se a desistência for por parte do CONTRATANTE haverá a perda do sinal dado.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>Parágrafo único.</strong> Caso o evento não possa ser realizado no dia e hora estipulada entre as partes por forças maiores e em casos fortuitos a data poderá ser alterada em comum acordo.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 16ª</strong> - Se a desistência for por parte da CONTRATADA, esta deverá ressarcir o valor do sinal dado pelo CONTRATANTE, salvo se ocorrer o previsto na cláusula 11ª, não cabendo, neste caso, nem a devolução de sinal.
            </p>
          </div>
        </div>

        <!-- Condições Gerais -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DAS CONDIÇÕES GERAIS
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 17ª</strong> - Fica compactuada entre as partes a total inexistência de vínculo trabalhista entre as partes contratantes, excluindo as obrigações previdenciárias e os encargos sociais, não havendo entre CONTRATADA e CONTRATANTE qualquer tipo de relação de subordinação.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 18ª</strong> - Salvo com a expressa autorização do CONTRATANTE, não pode a CONTRATADA transferir ou subcontratar os serviços previstos neste instrumento, sob o risco de ocorrer a rescisão imediata.
            </p>
          </div>

          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 19ª</strong> - AUTORIZO a contratante expor fotos do evento.
            </p>
          </div>
        </div>

        <!-- Foro -->
        <div style="margin-bottom: 4px;">
          <h2 style="font-weight: bold; font-size: 10pt; margin-bottom: 6px;">
            DO FORO
          </h2>
          
          <div style="margin-bottom: 4px;">
            <p style="text-align: justify; margin-bottom: 4px;">
              <strong>CLÁUSULA 20ª</strong> - Para dirimir quaisquer controvérsias oriundas do presente contrato, as partes elegem o foro da comarca de Manhuaçu/MG.
            </p>
          </div>
        </div>

        <!-- Fechamento -->
        <div style="margin-top: 32px; border-top: 1px solid #e2e8f0; padding-top: 20px;">
          <p style="text-align: center; margin-bottom: 15px;">
            Por estarem assim justos e contratados, firmam o presente instrumento, em duas vias de igual teor.
          </p>

          <!-- Local e Data -->
          <div style="text-align: center; margin-bottom: 32px;">
            <p>Manhuaçu, {{dataAtual}}.</p>
          </div>

          <!-- Assinaturas -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 20px;">
            <!-- Contratada -->
            <div style="text-align: center;">
              <div style="border-top: 2px solid #64748b; padding-top: 8px; margin-bottom: 4px;">
                <p style="font-weight: bold;">CONTRATADA:</p>
              </div>
              <p>RODRIGO PEREIRA DE OLIVEIRA</p>
              <p style="font-size: 10pt; color: #64748b;">CPF: 016.331.906-50</p>
            </div>

            <!-- Contratante -->
            <div style="text-align: center;">
              <div style="border-top: 2px solid #64748b; padding-top: 8px; margin-bottom: 4px;">
                <p style="font-weight: bold;">CONTRATANTE:</p>
              </div>
              <p>{{contratante}}</p>
              <p style="font-size: 10pt; color: #64748b;">CPF: {{cpf}}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Imagem de Rodapé - Posicionada absolutamente na parte inferior -->
      <img src="${baseUrl}/images/rodape.jpg" alt="Rodapé" style="position: absolute; bottom: 0; left: 0; width: 100%; height: auto; display: block;" onerror="this.style.display='none';" />
    </div>
  `;
  };

  // Carregar documento do Firebase
  const loadDocument = useCallback(async () => {
    try {
      const docRef = doc(db, 'contractDocuments', 'default');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as any;
        setDocument({
          id: data.id || 'default',
          title: data.title || 'Modelo de Contrato',
          content: data.content || '',
          pages: data.pages || [data.content || ''],
          currentPage: data.currentPage || 0,
          styling: data.styling || {
            fontFamily: 'Courier New',
            fontSize: 10,
            lineHeight: 1.4,
            textColor: '#000000',
            backgroundColor: '#ffffff',
            accentColor: '#0f9d8f'
          },
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString()
        });
      } else {
        // Se não existir, criar com conteúdo padrão
        const defaultContent = getDefaultContent();
        const newDoc: ContractDocument = {
          ...document,
          content: defaultContent,
          pages: [defaultContent],
          currentPage: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setDocument(newDoc);
        await setDoc(docRef, newDoc);
      }
    } catch (error) {
      console.error('Erro ao carregar documento:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Salvar documento no Firebase
  const saveDocument = useCallback(async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const docRef = doc(db, 'contractDocuments', 'default');
      const updatedDoc = {
        ...document,
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(docRef, updatedDoc);
      setDocument(updatedDoc);
    } catch (error) {
      console.error('Erro ao salvar documento:', error);
    } finally {
      setIsSaving(false);
    }
  }, [document, isSaving]);

  // Carregar documento na inicialização
  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  // Salvar automaticamente a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.content && !isSaving) {
        saveDocument();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [document.content, isSaving, saveDocument]);

  // Configuração do TinyMCE
  const editorConfig = {
    height: '297mm',
    width: '210mm',
    menubar: true,
    plugins: 'anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount',
    toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',
    content_style: `
      body { 
        font-family: 'Courier New', Courier, monospace; 
        font-size: 10pt; 
        line-height: 1.4; 
        color: #000000;
        background-color: #ffffff;
        margin: 0;
        padding: 12mm;
        overflow: hidden;
        height: 100%;
      }
      h1, h2, h3, h4, h5, h6 { 
        font-weight: bold; 
        margin-bottom: 6px; 
      }
      p { 
        text-align: justify; 
        margin-bottom: 4px; 
      }
      strong { 
        font-weight: bold; 
      }
    `,
    setup: (editor: any) => {
      editor.on('init', () => {
        const pages = document.pages || [];
        editor.setContent(pages[document.currentPage] || '');
      });
      editor.on('input change keyup', () => {
        const content = editor.getContent();
        updateCurrentPage(content);
      });
    }
  };

  // Função para inserir campo dinâmico
  const insertField = (field: string) => {
    if (editorRef.current) {
      editorRef.current.insertContent(`{{${field}}}`);
    }
  };

  // Função para restaurar template padrão
  const restoreDefault = () => {
    const defaultContent = getDefaultContent();
    setDocument(prev => ({
      ...prev,
      content: defaultContent,
      pages: [defaultContent],
      currentPage: 0,
      updatedAt: new Date().toISOString()
    }));
    
    // Atualizar o editor
    if (editorRef.current) {
      editorRef.current.setContent(defaultContent);
    }
    
    // Forçar re-render do editor
    setEditorKey(prev => prev + 1);
  };

  // Funções de gerenciamento de páginas
  const addNewPage = () => {
    setDocument(prev => ({
      ...prev,
      pages: [...prev.pages, ''],
      currentPage: prev.pages.length,
      updatedAt: new Date().toISOString()
    }));
  };

  const goToPage = (pageIndex: number) => {
    const pages = document.pages || [];
    if (pageIndex >= 0 && pageIndex < pages.length) {
      setDocument(prev => ({
        ...prev,
        currentPage: pageIndex,
        content: pages[pageIndex],
        updatedAt: new Date().toISOString()
      }));
      
      // Atualizar o editor
      if (editorRef.current) {
        editorRef.current.setContent(pages[pageIndex]);
      }
    }
  };

  const deletePage = (pageIndex: number) => {
    const pages = document.pages || [];
    if (pages.length > 1) {
      const newPages = pages.filter((_, index) => index !== pageIndex);
      const newCurrentPage = pageIndex >= newPages.length ? newPages.length - 1 : pageIndex;
      
      setDocument(prev => ({
        ...prev,
        pages: newPages,
        currentPage: newCurrentPage,
        content: newPages[newCurrentPage],
        updatedAt: new Date().toISOString()
      }));
      
      // Atualizar o editor
      if (editorRef.current) {
        editorRef.current.setContent(newPages[newCurrentPage]);
      }
    }
  };

  const updateCurrentPage = (content: string) => {
    setDocument(prev => {
      const pages = prev.pages || [];
      const newPages = [...pages];
      newPages[prev.currentPage] = content;
      return {
        ...prev,
        pages: newPages,
        content: content,
        updatedAt: new Date().toISOString()
      };
    });
  };

  // Função para copiar HTML
  const copyHTML = () => {
    navigator.clipboard.writeText(document.content);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Layout className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-semibold text-slate-800">
                Editor de Modelo de Contrato
              </h1>
            </div>
            <div className="text-sm text-slate-500">
              {isSaving ? 'Salvando...' : 'Salvo automaticamente'}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
            </button>
            
            <button
              onClick={() => setSplitView(!splitView)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <Layout className="w-4 h-4" />
              {splitView ? 'Modo Único' : 'Modo Dividido'}
            </button>
            
            <button
              onClick={() => setShowContractSelector(!showContractSelector)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <FileText className="w-4 h-4" />
              {selectedContractId ? 'Dados Reais' : 'Testar com Dados'}
            </button>
            
            <button
              onClick={saveDocument}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              Salvar
            </button>
          </div>
        </div>
      </div>

      {/* Seletor de Contratos */}
      {showContractSelector && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-sm font-semibold text-blue-800">Testar com Dados Reais</h3>
              <select
                value={selectedContractId}
                onChange={(e) => setSelectedContractId(e.target.value)}
                className="px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={contractsLoading}
              >
                <option value="">Selecione um contrato...</option>
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contratante} - {contract.nomeAniversariante} ({contract.dataFesta})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              {selectedContractId && (
                <button
                  onClick={() => setSelectedContractId('')}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-100 rounded"
                >
                  Limpar Seleção
                </button>
              )}
              <button
                onClick={() => setShowContractSelector(false)}
                className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-100 rounded"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navegador de Páginas */}
      <div className="bg-slate-100 border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(document.currentPage - 1)}
              disabled={document.currentPage === 0}
              className="flex items-center gap-1 px-2 py-1 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>
            
            <div className="flex items-center gap-1">
              {(document.pages || []).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index)}
                  className={`w-8 h-8 text-xs font-medium rounded ${
                    index === document.currentPage
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => goToPage(document.currentPage + 1)}
              disabled={document.currentPage === (document.pages || []).length - 1}
              className="flex items-center gap-1 px-2 py-1 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">
              Página {document.currentPage + 1} de {(document.pages || []).length}
            </span>
            
            <button
              onClick={addNewPage}
              className="flex items-center gap-1 px-3 py-1 text-sm text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-200 rounded"
            >
              <PlusCircle className="w-4 h-4" />
              Nova Página
            </button>
            
            {(document.pages || []).length > 1 && (
              <button
                onClick={() => deletePage(document.currentPage)}
                className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 bg-white hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-180px)]">
        {/* Sidebar */}
        <div className="w-80 bg-white border-r border-slate-200 p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* Configurações do Documento */}
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Configurações do Documento</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Título
                  </label>
                  <input
                    type="text"
                    value={document.title}
                    onChange={(e) => setDocument(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Fonte
                  </label>
                  <select
                    value={document.styling.fontFamily}
                    onChange={(e) => setDocument(prev => ({ 
                      ...prev, 
                      styling: { ...prev.styling, fontFamily: e.target.value }
                    }))}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Courier New">Courier New</option>
                    <option value="Arial">Arial</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Calibri">Calibri</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Tamanho da Fonte
                  </label>
                  <input
                    type="number"
                    value={document.styling.fontSize}
                    onChange={(e) => setDocument(prev => ({ 
                      ...prev, 
                      styling: { ...prev.styling, fontSize: parseInt(e.target.value) }
                    }))}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Campos Dinâmicos */}
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Campos Dinâmicos</h3>
              <div className="space-y-3">
                {/* Campos Principais */}
                <div>
                  <h4 className="text-xs font-medium text-slate-600 mb-2">Informações Pessoais</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['contratante', 'nacionalidade', 'estadoCivil', 'profissao', 'identidade', 'cpf', 'telefone'].map((field) => (
                      <button
                        key={field}
                        onClick={() => insertField(field)}
                        className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded border border-slate-300 transition-colors"
                      >
                        {field}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Endereço */}
                <div>
                  <h4 className="text-xs font-medium text-slate-600 mb-2">Endereço</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['endereco', 'numero', 'complemento', 'bairro', 'cep', 'cidade', 'estado'].map((field) => (
                      <button
                        key={field}
                        onClick={() => insertField(field)}
                        className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded border border-slate-300 transition-colors"
                      >
                        {field}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Festa */}
                <div>
                  <h4 className="text-xs font-medium text-slate-600 mb-2">Festa</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['dataFesta', 'horarioInicioFesta', 'enderecoFesta', 'inicioRecreacao', 'fimRecreacao', 'numeroConvidados', 'nomeAniversariante', 'temaFesta', 'restricaoMusical', 'pacoteAtividades'].map((field) => (
                      <button
                        key={field}
                        onClick={() => insertField(field)}
                        className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded border border-slate-300 transition-colors"
                      >
                        {field}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Valores */}
                <div>
                  <h4 className="text-xs font-medium text-slate-600 mb-2">Valores</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['valor', 'valorPorExtenso', 'dataAtual'].map((field) => (
                      <button
                        key={field}
                        onClick={() => insertField(field)}
                        className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded border border-slate-300 transition-colors"
                      >
                        {field}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Formatação Rápida */}
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Formatação Rápida</h3>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                  onClick={() => editorRef.current?.execCommand('bold')}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <Bold className="w-4 h-4" />
                  Negrito
                </button>
                <button
                  onClick={() => editorRef.current?.execCommand('italic')}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <Italic className="w-4 h-4" />
                  Itálico
                </button>
                <button
                  onClick={() => editorRef.current?.execCommand('underline')}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <Underline className="w-4 h-4" />
                  Sublinhado
                </button>
                <button
                  onClick={() => editorRef.current?.execCommand('justifyFull')}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <AlignJustify className="w-4 h-4" />
                  Justificar
                </button>
              </div>
            </div>

            {/* Ações Rápidas */}
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Ações Rápidas</h3>
              <div className="space-y-2">
                <button
                  onClick={restoreDefault}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Restaurar Padrão
                </button>
                
                <button
                  onClick={copyHTML}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copiar HTML
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Editor Principal */}
        <div className="flex-1 flex">
          {/* Editor TinyMCE */}
          <div className={`${splitView && showPreview ? 'w-1/2' : 'w-full'} border-r border-slate-200`}>
            <div className="h-full">
              <Editor
                key={editorKey}
                apiKey='ig4x2og0mvca4pazpfwfzvnseruk70boe2wnmu360v3dn0i5'
                onInit={(_evt: any, editor: any) => editorRef.current = editor}
                value={document.content}
                onEditorChange={(content: string) => {
                  setDocument(prev => ({
                    ...prev,
                    content: content,
                    updatedAt: new Date().toISOString()
                  }));
                }}
                init={editorConfig}
              />
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className={`${splitView ? 'w-1/2' : 'w-full'} p-6 bg-slate-50 overflow-y-auto`}>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Preview do Contrato</h3>
                <div className="border border-slate-300 rounded-md overflow-hidden">
                  <Editor
                    apiKey='ig4x2og0mvca4pazpfwfzvnseruk70boe2wnmu360v3dn0i5'
                    init={{
                      height: '297mm',
                      width: '210mm',
                      menubar: false,
                      toolbar: false,
                      plugins: [
                        'advlist autolink lists link image charmap print preview anchor',
                        'searchreplace visualblocks code fullscreen',
                        'insertdatetime media table paste code help wordcount'
                      ],
                      content_style: `
                        body { 
                          font-family: 'Courier New', Courier, monospace; 
                          font-size: 10pt; 
                          line-height: 1.4; 
                          color: #000000; 
                          background-color: #ffffff; 
                          margin: 0; 
                          padding: 12mm; 
                          text-align: justify;
                          word-wrap: break-word;
                          overflow-wrap: break-word;
                          hyphens: none;
                          white-space: normal;
                          word-break: normal;
                          text-justify: inter-word;
                          -webkit-hyphens: none;
                          -moz-hyphens: none;
                          -ms-hyphens: none;
                          overflow: hidden;
                          height: 100%;
                        }
                        p {
                          margin-bottom: 4px;
                        }
                        h1, h2, h3, h4, h5, h6 {
                          font-weight: bold;
                          margin-bottom: 6px;
                        }
                        img {
                          max-width: 100%;
                          height: auto;
                          display: block;
                        }
                        ul, ol {
                          margin-bottom: 4px;
                          padding-left: 20px;
                        }
                        li {
                          margin-bottom: 2px;
                        }
                      `,
                      branding: false,
                      statusbar: false,
                      resize: false,
                      elementpath: false,
                      contextmenu: false,
                      paste_data_images: false,
                      paste_auto_cleanup_on_paste: true,
                      paste_remove_styles_if_webkit: false,
                      paste_remove_styles: false,
                      paste_retain_style_properties: 'all',
                      paste_merge_formats: true,
                      paste_convert_word_fake_lists: true,
                      paste_enable_default_filters: true
                    }}
                    value={getPreviewContent((document.pages || [])[document.currentPage] || '')}
                    disabled={true}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractTemplateEditor;